import { BaseObj } from '@/objects/BaseObj';
import { useBaseObjEntityStateHandler } from '@/objects/entitystatehandler/entitystatehandler';
import { isRef, ref, unref } from 'vue';
import { TimeSpan, type ITimeSpan } from '@/objects/TimeSpan';
import { StationRoleOverride } from './StationRoleOverride';
import type { EntityState } from './../enums/EntityState';
import type { StationRoleGPIOoperation } from './../enums/StationRoleGPIOoperation';
import { formatDateIso } from '@/utilities/dateutils';

export class StationRole extends BaseObj {
    public ID: number;
    public __id: number;

    public AcceptChipForRelayLegnumber: number | null;
    public __acceptchipforrelaylegnumber: number | null;

    public AllowNegativeStart: boolean;
    public __allownegativestart: boolean;

    public AllowOnlyGPIOOrManual: boolean;
    public __allowonlygpioormanual: boolean;

    public AssignLapPoints: number | null;
    public __assignlappoints: number | null;

    public AutomaticallyChangeRace: boolean;
    public __automaticallychangerace: boolean;

    public EventID: number;
    public __eventid: number;

    public Finish: boolean;
    public __finish: boolean;

    public GPI1Operation: StationRoleGPIOoperation;
    public __gpi1operation: StationRoleGPIOoperation;

    public GPI1Value1: number;
    public __gpi1value1: number;

    public GPI1Value2: number;
    public __gpi1value2: number;

    public GPI2Operation: StationRoleGPIOoperation;
    public __gpi2operation: StationRoleGPIOoperation;

    public GPI2Value1: number;
    public __gpi2value1: number;

    public GPI2Value2: number;
    public __gpi2value2: number;

    public GPI3Operation: StationRoleGPIOoperation;
    public __gpi3operation: StationRoleGPIOoperation;

    public GPI3Value1: number;
    public __gpi3value1: number;

    public GPI3Value2: number;
    public __gpi3value2: number;

    public GPI4Operation: StationRoleGPIOoperation;
    public __gpi4operation: StationRoleGPIOoperation;

    public GPI4Value1: number;
    public __gpi4value1: number;

    public GPI4Value2: number;
    public __gpi4value2: number;

    public GPSCreatePassRadius: number | null;
    public __gpscreatepassradius: number | null;

    public IgnoreInSplitresult: boolean;
    public __ignoreinsplitresult: boolean;

    public IsNeutralZoneIn: boolean;
    public __isneutralzonein: boolean;

    public IsNeutralZoneOut: boolean;
    public __isneutralzoneout: boolean;

    public IsShootingStation: boolean;
    public __isshootingstation: boolean;

    public Km: number;
    public __km: number;

    public Mandatory: boolean;
    public __mandatory: boolean;

    public MaxLapTime: ITimeSpan | null;
    public __maxlaptime: ITimeSpan | null;

    public MinLapTime: ITimeSpan;
    public __minlaptime: ITimeSpan;

    public Name: string;
    public __name: string;

    public Overrides: StationRoleOverride[];
    public __overrides: StationRoleOverride[];

    public PhotofinishMilliseconds: number | null;
    public __photofinishmilliseconds: number | null;

    public Pretiming: boolean;
    public __pretiming: boolean;

    public PrintOnResultlist: boolean;
    public __printonresultlist: boolean;

    public Public: boolean;
    public __public: boolean;

    public RaceID: number | null;
    public __raceid: number | null;

    public RelayLegnumber: number | null;
    public __relaylegnumber: number | null;

    public SmoothingInSeconds: number;
    public __smoothinginseconds: number;

    public Sort: number;
    public __sort: number;

    public Start: boolean;
    public __start: boolean;

    public StartControl: boolean;
    public __startcontrol: boolean;

    public StartControlMax: number | null;
    public __startcontrolmax: number | null;

    public StartControlMin: number | null;
    public __startcontrolmin: number | null;

    public StartNet: boolean;
    public __startnet: boolean;

    public StartTime: Date;
    public __starttime: Date;

    public StationID: number;
    public __stationid: number;

    public StopTime: Date;
    public __stoptime: Date;

    public TimeSelect: number = 1;
    public __timeselect: number = 1;

    public __entityStateHandler;
    public __selfEntityState: EntityState;
    public __entityState: any;
    public get EntityState(): EntityState {
        return unref(this.__entityState);
    }
    /***
     * @deprecated Use the static FromJsValues method instead of the constructor to get type safety and default values
     */
    public constructor(values: {
        ID?: number;
        AcceptChipForRelayLegnumber?: number | null;
        AllowNegativeStart?: boolean;
        AllowOnlyGPIOOrManual?: boolean;
        AssignLapPoints?: number | null;
        AutomaticallyChangeRace?: boolean;
        EntityState?: EntityState;
        EventID?: number;
        Finish?: boolean;
        GPI1Operation?: StationRoleGPIOoperation;
        GPI1Value1?: number;
        GPI1Value2?: number;
        GPI2Operation?: StationRoleGPIOoperation;
        GPI2Value1?: number;
        GPI2Value2?: number;
        GPI3Operation?: StationRoleGPIOoperation;
        GPI3Value1?: number;
        GPI3Value2?: number;
        GPI4Operation?: StationRoleGPIOoperation;
        GPI4Value1?: number;
        GPI4Value2?: number;
        GPSCreatePassRadius?: number | null;
        IgnoreInSplitresult?: boolean;
        IsNeutralZoneIn?: boolean;
        IsNeutralZoneOut?: boolean;
        IsShootingStation?: boolean;
        Km?: number;
        Mandatory?: boolean;
        MaxLapTime?: ITimeSpan | null;
        MinLapTime?: ITimeSpan;
        Name?: string | null;
        Overrides?: StationRoleOverride[];
        PhotofinishMilliseconds?: number | null;
        Pretiming?: boolean;
        PrintOnResultlist?: boolean;
        Public?: boolean;
        RaceID?: number | null;
        RelayLegnumber?: number | null;
        SmoothingInSeconds?: number;
        Sort?: number;
        Start?: boolean;
        StartControl?: boolean;
        StartControlMax?: number | null;
        StartControlMin?: number | null;
        StartNet?: boolean;
        StartTime?: Date;
        StationID?: number;
        StopTime?: Date;
        TimeSelect?: number;
    }) {
        super();
        this.__entityState = ref(values.EntityState ?? 'Unchanged');
        this.__selfEntityState = values.EntityState ?? 'Unchanged';
        this.__entityStateHandler = useBaseObjEntityStateHandler<StationRole>(unref(this.__entityState), (states) => {
            if (isRef(this.__entityState)) {
                this.__entityState.value = states.EntityStateWithChildrenConsidered;
            } else {
                console.warn('StationRole tried to set entitystate, but entitystate is not a ref! This should never happen');
            }
            this.__selfEntityState = states.SelfEntityState;
        });
        this.ID = values.ID ?? 0;
        this.__id = values.ID ?? 0;
        Object.defineProperty(this, 'ID', {
            get(): number {
                return this.__id;
            },
            enumerable: true,
            configurable: true,
        });

        this.AcceptChipForRelayLegnumber = values.AcceptChipForRelayLegnumber ?? null;
        this.__acceptchipforrelaylegnumber = values.AcceptChipForRelayLegnumber ?? null;
        Object.defineProperty(this, 'AcceptChipForRelayLegnumber', {
            get(): number | null {
                return this.__acceptchipforrelaylegnumber;
            },
            set(value: number | null) {
                this.__entityStateHandler.propertyChanged('AcceptChipForRelayLegnumber', this.__acceptchipforrelaylegnumber, value);
                this.__acceptchipforrelaylegnumber = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.AllowNegativeStart = values.AllowNegativeStart ?? false;
        this.__allownegativestart = values.AllowNegativeStart ?? false;
        Object.defineProperty(this, 'AllowNegativeStart', {
            get(): boolean {
                return this.__allownegativestart;
            },
            set(value: boolean) {
                this.__entityStateHandler.propertyChanged('AllowNegativeStart', this.__allownegativestart, value);
                this.__allownegativestart = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.AllowOnlyGPIOOrManual = values.AllowOnlyGPIOOrManual ?? false;
        this.__allowonlygpioormanual = values.AllowOnlyGPIOOrManual ?? false;
        Object.defineProperty(this, 'AllowOnlyGPIOOrManual', {
            get(): boolean {
                return this.__allowonlygpioormanual;
            },
            set(value: boolean) {
                this.__entityStateHandler.propertyChanged('AllowOnlyGPIOOrManual', this.__allowonlygpioormanual, value);
                this.__allowonlygpioormanual = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.AssignLapPoints = values.AssignLapPoints ?? null;
        this.__assignlappoints = values.AssignLapPoints ?? null;
        Object.defineProperty(this, 'AssignLapPoints', {
            get(): number | null {
                return this.__assignlappoints;
            },
            set(value: number | null) {
                this.__entityStateHandler.propertyChanged('AssignLapPoints', this.__assignlappoints, value);
                this.__assignlappoints = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.AutomaticallyChangeRace = values.AutomaticallyChangeRace ?? false;
        this.__automaticallychangerace = values.AutomaticallyChangeRace ?? false;
        Object.defineProperty(this, 'AutomaticallyChangeRace', {
            get(): boolean {
                return this.__automaticallychangerace;
            },
            set(value: boolean) {
                this.__entityStateHandler.propertyChanged('AutomaticallyChangeRace', this.__automaticallychangerace, value);
                this.__automaticallychangerace = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.EventID = values.EventID ?? 0;
        this.__eventid = values.EventID ?? 0;
        Object.defineProperty(this, 'EventID', {
            get(): number {
                return this.__eventid;
            },
            set(value: number) {
                this.__entityStateHandler.propertyChanged('EventID', this.__eventid, value);
                this.__eventid = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.Finish = values.Finish ?? false;
        this.__finish = values.Finish ?? false;
        Object.defineProperty(this, 'Finish', {
            get(): boolean {
                return this.__finish;
            },
            set(value: boolean) {
                this.__entityStateHandler.propertyChanged('Finish', this.__finish, value);
                this.__finish = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.GPI1Operation = values.GPI1Operation ?? 'None';
        this.__gpi1operation = values.GPI1Operation ?? 'None';
        Object.defineProperty(this, 'GPI1Operation', {
            get(): StationRoleGPIOoperation {
                return this.__gpi1operation;
            },
            set(value: StationRoleGPIOoperation) {
                this.__entityStateHandler.propertyChanged('GPI1Operation', this.__gpi1operation, value);
                this.__gpi1operation = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.GPI1Value1 = values.GPI1Value1 ?? 0;
        this.__gpi1value1 = values.GPI1Value1 ?? 0;
        Object.defineProperty(this, 'GPI1Value1', {
            get(): number {
                return this.__gpi1value1;
            },
            set(value: number) {
                this.__entityStateHandler.propertyChanged('GPI1Value1', this.__gpi1value1, value);
                this.__gpi1value1 = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.GPI1Value2 = values.GPI1Value2 ?? 0;
        this.__gpi1value2 = values.GPI1Value2 ?? 0;
        Object.defineProperty(this, 'GPI1Value2', {
            get(): number {
                return this.__gpi1value2;
            },
            set(value: number) {
                this.__entityStateHandler.propertyChanged('GPI1Value2', this.__gpi1value2, value);
                this.__gpi1value2 = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.GPI2Operation = values.GPI2Operation ?? 'None';
        this.__gpi2operation = values.GPI2Operation ?? 'None';
        Object.defineProperty(this, 'GPI2Operation', {
            get(): StationRoleGPIOoperation {
                return this.__gpi2operation;
            },
            set(value: StationRoleGPIOoperation) {
                this.__entityStateHandler.propertyChanged('GPI2Operation', this.__gpi2operation, value);
                this.__gpi2operation = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.GPI2Value1 = values.GPI2Value1 ?? 0;
        this.__gpi2value1 = values.GPI2Value1 ?? 0;
        Object.defineProperty(this, 'GPI2Value1', {
            get(): number {
                return this.__gpi2value1;
            },
            set(value: number) {
                this.__entityStateHandler.propertyChanged('GPI2Value1', this.__gpi2value1, value);
                this.__gpi2value1 = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.GPI2Value2 = values.GPI2Value2 ?? 0;
        this.__gpi2value2 = values.GPI2Value2 ?? 0;
        Object.defineProperty(this, 'GPI2Value2', {
            get(): number {
                return this.__gpi2value2;
            },
            set(value: number) {
                this.__entityStateHandler.propertyChanged('GPI2Value2', this.__gpi2value2, value);
                this.__gpi2value2 = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.GPI3Operation = values.GPI3Operation ?? 'None';
        this.__gpi3operation = values.GPI3Operation ?? 'None';
        Object.defineProperty(this, 'GPI3Operation', {
            get(): StationRoleGPIOoperation {
                return this.__gpi3operation;
            },
            set(value: StationRoleGPIOoperation) {
                this.__entityStateHandler.propertyChanged('GPI3Operation', this.__gpi3operation, value);
                this.__gpi3operation = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.GPI3Value1 = values.GPI3Value1 ?? 0;
        this.__gpi3value1 = values.GPI3Value1 ?? 0;
        Object.defineProperty(this, 'GPI3Value1', {
            get(): number {
                return this.__gpi3value1;
            },
            set(value: number) {
                this.__entityStateHandler.propertyChanged('GPI3Value1', this.__gpi3value1, value);
                this.__gpi3value1 = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.GPI3Value2 = values.GPI3Value2 ?? 0;
        this.__gpi3value2 = values.GPI3Value2 ?? 0;
        Object.defineProperty(this, 'GPI3Value2', {
            get(): number {
                return this.__gpi3value2;
            },
            set(value: number) {
                this.__entityStateHandler.propertyChanged('GPI3Value2', this.__gpi3value2, value);
                this.__gpi3value2 = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.GPI4Operation = values.GPI4Operation ?? 'None';
        this.__gpi4operation = values.GPI4Operation ?? 'None';
        Object.defineProperty(this, 'GPI4Operation', {
            get(): StationRoleGPIOoperation {
                return this.__gpi4operation;
            },
            set(value: StationRoleGPIOoperation) {
                this.__entityStateHandler.propertyChanged('GPI4Operation', this.__gpi4operation, value);
                this.__gpi4operation = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.GPI4Value1 = values.GPI4Value1 ?? 0;
        this.__gpi4value1 = values.GPI4Value1 ?? 0;
        Object.defineProperty(this, 'GPI4Value1', {
            get(): number {
                return this.__gpi4value1;
            },
            set(value: number) {
                this.__entityStateHandler.propertyChanged('GPI4Value1', this.__gpi4value1, value);
                this.__gpi4value1 = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.GPI4Value2 = values.GPI4Value2 ?? 0;
        this.__gpi4value2 = values.GPI4Value2 ?? 0;
        Object.defineProperty(this, 'GPI4Value2', {
            get(): number {
                return this.__gpi4value2;
            },
            set(value: number) {
                this.__entityStateHandler.propertyChanged('GPI4Value2', this.__gpi4value2, value);
                this.__gpi4value2 = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.GPSCreatePassRadius = values.GPSCreatePassRadius ?? null;
        this.__gpscreatepassradius = values.GPSCreatePassRadius ?? null;
        Object.defineProperty(this, 'GPSCreatePassRadius', {
            get(): number | null {
                return this.__gpscreatepassradius;
            },
            set(value: number | null) {
                this.__entityStateHandler.propertyChanged('GPSCreatePassRadius', this.__gpscreatepassradius, value);
                this.__gpscreatepassradius = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.IgnoreInSplitresult = values.IgnoreInSplitresult ?? false;
        this.__ignoreinsplitresult = values.IgnoreInSplitresult ?? false;
        Object.defineProperty(this, 'IgnoreInSplitresult', {
            get(): boolean {
                return this.__ignoreinsplitresult;
            },
            set(value: boolean) {
                this.__entityStateHandler.propertyChanged('IgnoreInSplitresult', this.__ignoreinsplitresult, value);
                this.__ignoreinsplitresult = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.IsNeutralZoneIn = values.IsNeutralZoneIn ?? false;
        this.__isneutralzonein = values.IsNeutralZoneIn ?? false;
        Object.defineProperty(this, 'IsNeutralZoneIn', {
            get(): boolean {
                return this.__isneutralzonein;
            },
            set(value: boolean) {
                this.__entityStateHandler.propertyChanged('IsNeutralZoneIn', this.__isneutralzonein, value);
                this.__isneutralzonein = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.IsNeutralZoneOut = values.IsNeutralZoneOut ?? false;
        this.__isneutralzoneout = values.IsNeutralZoneOut ?? false;
        Object.defineProperty(this, 'IsNeutralZoneOut', {
            get(): boolean {
                return this.__isneutralzoneout;
            },
            set(value: boolean) {
                this.__entityStateHandler.propertyChanged('IsNeutralZoneOut', this.__isneutralzoneout, value);
                this.__isneutralzoneout = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.IsShootingStation = values.IsShootingStation ?? false;
        this.__isshootingstation = values.IsShootingStation ?? false;
        Object.defineProperty(this, 'IsShootingStation', {
            get(): boolean {
                return this.__isshootingstation;
            },
            set(value: boolean) {
                this.__entityStateHandler.propertyChanged('IsShootingStation', this.__isshootingstation, value);
                this.__isshootingstation = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.Km = values.Km ?? 0;
        this.__km = values.Km ?? 0;
        Object.defineProperty(this, 'Km', {
            get(): number {
                return this.__km;
            },
            set(value: number) {
                this.__entityStateHandler.propertyChanged('Km', this.__km, value);
                this.__km = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.Mandatory = values.Mandatory ?? false;
        this.__mandatory = values.Mandatory ?? false;
        Object.defineProperty(this, 'Mandatory', {
            get(): boolean {
                return this.__mandatory;
            },
            set(value: boolean) {
                this.__entityStateHandler.propertyChanged('Mandatory', this.__mandatory, value);
                this.__mandatory = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.MaxLapTime = values.MaxLapTime ?? null;
        this.__maxlaptime = values.MaxLapTime ?? null;
        Object.defineProperty(this, 'MaxLapTime', {
            get(): ITimeSpan | null {
                return this.__maxlaptime;
            },
            set(value: ITimeSpan | null) {
                this.__entityStateHandler.propertyChanged('MaxLapTime', this.__maxlaptime, value);
                this.__maxlaptime = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.MinLapTime = values.MinLapTime ?? TimeSpan.Empty();
        this.__minlaptime = values.MinLapTime ?? TimeSpan.Empty();
        Object.defineProperty(this, 'MinLapTime', {
            get(): ITimeSpan {
                return this.__minlaptime;
            },
            set(value: ITimeSpan) {
                this.__entityStateHandler.propertyChanged('MinLapTime', this.__minlaptime, value);
                this.__minlaptime = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.Name = values.Name ?? '';
        this.__name = values.Name ?? '';
        Object.defineProperty(this, 'Name', {
            get(): string {
                return this.__name;
            },
            set(value: string) {
                this.__entityStateHandler.propertyChanged('Name', this.__name, value);
                this.__name = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.Overrides = values.Overrides ?? [];
        this.__overrides = this.__entityStateHandler.createArrayWithMutationCallback({
            initalValue: values.Overrides ?? [],
            callback: (_oldVal, newVal) => this.__entityStateHandler.childArrayOfBaseObjsChanged('Overrides', newVal),
        });
        this.__overrides.forEach((child) => {
            child._setNotifyParentOfModificationCallback(() =>
                this.__entityStateHandler.childArrayOfBaseObjsChanged('Overrides', this.Overrides),
            );
        });
        Object.defineProperty(this, 'Overrides', {
            get(): StationRoleOverride[] {
                return this.__overrides;
            },
            set(value: StationRoleOverride[]) {
                // TODO: Here we could validate that the new and the old value has all the same objects. If we dont we have a bug
                this.__overrides = this.__entityStateHandler.createArrayWithMutationCallback({
                    initalValue: value ?? [],
                    callback: (_oldVal: StationRoleOverride[], newVal: StationRoleOverride[]) =>
                        this.__entityStateHandler.childArrayOfBaseObjsChanged('Overrides', newVal),
                });
                this.__overrides.forEach((child: StationRoleOverride) => {
                    child._setNotifyParentOfModificationCallback(() =>
                        this.__entityStateHandler.childArrayOfBaseObjsChanged('Overrides', this.Overrides),
                    );
                });
                this.__entityStateHandler.childArrayOfBaseObjsChanged('Overrides', this.Overrides);
            },
            enumerable: true,
            configurable: true,
        });

        this.PhotofinishMilliseconds = values.PhotofinishMilliseconds ?? null;
        this.__photofinishmilliseconds = values.PhotofinishMilliseconds ?? null;
        Object.defineProperty(this, 'PhotofinishMilliseconds', {
            get(): number | null {
                return this.__photofinishmilliseconds;
            },
            set(value: number | null) {
                this.__entityStateHandler.propertyChanged('PhotofinishMilliseconds', this.__photofinishmilliseconds, value);
                this.__photofinishmilliseconds = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.Pretiming = values.Pretiming ?? false;
        this.__pretiming = values.Pretiming ?? false;
        Object.defineProperty(this, 'Pretiming', {
            get(): boolean {
                return this.__pretiming;
            },
            set(value: boolean) {
                this.__entityStateHandler.propertyChanged('Pretiming', this.__pretiming, value);
                this.__pretiming = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.PrintOnResultlist = values.PrintOnResultlist ?? false;
        this.__printonresultlist = values.PrintOnResultlist ?? false;
        Object.defineProperty(this, 'PrintOnResultlist', {
            get(): boolean {
                return this.__printonresultlist;
            },
            set(value: boolean) {
                this.__entityStateHandler.propertyChanged('PrintOnResultlist', this.__printonresultlist, value);
                this.__printonresultlist = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.Public = values.Public ?? false;
        this.__public = values.Public ?? false;
        Object.defineProperty(this, 'Public', {
            get(): boolean {
                return this.__public;
            },
            set(value: boolean) {
                this.__entityStateHandler.propertyChanged('Public', this.__public, value);
                this.__public = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.RaceID = values.RaceID ?? null;
        this.__raceid = values.RaceID ?? null;
        Object.defineProperty(this, 'RaceID', {
            get(): number | null {
                return this.__raceid;
            },
            set(value: number | null) {
                this.__entityStateHandler.propertyChanged('RaceID', this.__raceid, value);
                this.__raceid = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.RelayLegnumber = values.RelayLegnumber ?? null;
        this.__relaylegnumber = values.RelayLegnumber ?? null;
        Object.defineProperty(this, 'RelayLegnumber', {
            get(): number | null {
                return this.__relaylegnumber;
            },
            set(value: number | null) {
                this.__entityStateHandler.propertyChanged('RelayLegnumber', this.__relaylegnumber, value);
                this.__relaylegnumber = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.SmoothingInSeconds = values.SmoothingInSeconds ?? 0;
        this.__smoothinginseconds = values.SmoothingInSeconds ?? 0;
        Object.defineProperty(this, 'SmoothingInSeconds', {
            get(): number {
                return this.__smoothinginseconds;
            },
            set(value: number) {
                this.__entityStateHandler.propertyChanged('SmoothingInSeconds', this.__smoothinginseconds, value);
                this.__smoothinginseconds = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.Sort = values.Sort ?? 0;
        this.__sort = values.Sort ?? 0;
        Object.defineProperty(this, 'Sort', {
            get(): number {
                return this.__sort;
            },
            set(value: number) {
                this.__entityStateHandler.propertyChanged('Sort', this.__sort, value);
                this.__sort = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.Start = values.Start ?? false;
        this.__start = values.Start ?? false;
        Object.defineProperty(this, 'Start', {
            get(): boolean {
                return this.__start;
            },
            set(value: boolean) {
                this.__entityStateHandler.propertyChanged('Start', this.__start, value);
                this.__start = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.StartControl = values.StartControl ?? false;
        this.__startcontrol = values.StartControl ?? false;
        Object.defineProperty(this, 'StartControl', {
            get(): boolean {
                return this.__startcontrol;
            },
            set(value: boolean) {
                this.__entityStateHandler.propertyChanged('StartControl', this.__startcontrol, value);
                this.__startcontrol = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.StartControlMax = values.StartControlMax ?? null;
        this.__startcontrolmax = values.StartControlMax ?? null;
        Object.defineProperty(this, 'StartControlMax', {
            get(): number | null {
                return this.__startcontrolmax;
            },
            set(value: number | null) {
                this.__entityStateHandler.propertyChanged('StartControlMax', this.__startcontrolmax, value);
                this.__startcontrolmax = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.StartControlMin = values.StartControlMin ?? null;
        this.__startcontrolmin = values.StartControlMin ?? null;
        Object.defineProperty(this, 'StartControlMin', {
            get(): number | null {
                return this.__startcontrolmin;
            },
            set(value: number | null) {
                this.__entityStateHandler.propertyChanged('StartControlMin', this.__startcontrolmin, value);
                this.__startcontrolmin = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.StartNet = values.StartNet ?? false;
        this.__startnet = values.StartNet ?? false;
        Object.defineProperty(this, 'StartNet', {
            get(): boolean {
                return this.__startnet;
            },
            set(value: boolean) {
                this.__entityStateHandler.propertyChanged('StartNet', this.__startnet, value);
                this.__startnet = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.StartTime = values.StartTime ?? new Date('1970-01-01T00:00:00.000');
        this.__starttime = values.StartTime ?? new Date('1970-01-01T00:00:00.000');
        Object.defineProperty(this, 'StartTime', {
            get(): Date {
                return this.__starttime;
            },
            set(value: Date) {
                this.__entityStateHandler.propertyChanged('StartTime', this.__starttime, value);
                this.__starttime = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.StationID = values.StationID ?? 0;
        this.__stationid = values.StationID ?? 0;
        Object.defineProperty(this, 'StationID', {
            get(): number {
                return this.__stationid;
            },
            set(value: number) {
                this.__entityStateHandler.propertyChanged('StationID', this.__stationid, value);
                this.__stationid = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.StopTime = values.StopTime ?? new Date('1970-01-01T00:00:00.000');
        this.__stoptime = values.StopTime ?? new Date('1970-01-01T00:00:00.000');
        Object.defineProperty(this, 'StopTime', {
            get(): Date {
                return this.__stoptime;
            },
            set(value: Date) {
                this.__entityStateHandler.propertyChanged('StopTime', this.__stoptime, value);
                this.__stoptime = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.TimeSelect = values.TimeSelect ?? 1;
        this.__timeselect = values.TimeSelect ?? 1;
        Object.defineProperty(this, 'TimeSelect', {
            get(): number {
                return this.__timeselect;
            },
            set(value: number) {
                this.__entityStateHandler.propertyChanged('TimeSelect', this.__timeselect, value);
                this.__timeselect = value;
            },
            enumerable: true,
            configurable: true,
        });
    }

    public _setNotifyParentOfModificationCallback(callback: () => void) {
        this.__entityStateHandler.setNotifyParentOfModificationCallback(callback);
    }

    public serialize(): string {
        return (
            '{' +
            `"ID": ${JSON.stringify(this.ID)}` +
            ',' +
            `"AcceptChipForRelayLegnumber": ${JSON.stringify(this.AcceptChipForRelayLegnumber)}` +
            ',' +
            `"AllowNegativeStart": ${JSON.stringify(this.AllowNegativeStart)}` +
            ',' +
            `"AllowOnlyGPIOOrManual": ${JSON.stringify(this.AllowOnlyGPIOOrManual)}` +
            ',' +
            `"AssignLapPoints": ${JSON.stringify(this.AssignLapPoints)}` +
            ',' +
            `"AutomaticallyChangeRace": ${JSON.stringify(this.AutomaticallyChangeRace)}` +
            ',' +
            `"EntityState": ${unref(this.__selfEntityState) == 'Unchanged' ? 0 : unref(this.__selfEntityState) == 'Deleted' ? 1 : unref(this.__selfEntityState) == 'Modified' ? 2 : unref(this.__selfEntityState) == 'Added' ? 3 : 0}` +
            ',' +
            `"EventID": ${JSON.stringify(this.EventID)}` +
            ',' +
            `"Finish": ${JSON.stringify(this.Finish)}` +
            ',' +
            `"GPI1Operation": ${this.GPI1Operation == 'None' ? 0 : this.GPI1Operation == 'AttachToTime' ? 1 : this.GPI1Operation == 'SetStartTimeNextGroup' ? 2 : this.GPI1Operation == 'SetStartTimeParticipant' ? 4 : this.GPI1Operation == 'SetStartTimeNextHeat' ? 5 : this.GPI1Operation == 'AttachAndRoundToWholeMinute' ? 6 : this.GPI1Operation == 'SetStartTimeParticipantWithBaasEqPort' ? 7 : this.GPI1Operation == 'SetStartTimeNextArmedHeat' ? 8 : this.GPI1Operation == 'SetStartNextParticipantOrNextArmedHeat' ? 9 : 0}` +
            ',' +
            `"GPI1Value1": ${JSON.stringify(this.GPI1Value1)}` +
            ',' +
            `"GPI1Value2": ${JSON.stringify(this.GPI1Value2)}` +
            ',' +
            `"GPI2Operation": ${this.GPI2Operation == 'None' ? 0 : this.GPI2Operation == 'AttachToTime' ? 1 : this.GPI2Operation == 'SetStartTimeNextGroup' ? 2 : this.GPI2Operation == 'SetStartTimeParticipant' ? 4 : this.GPI2Operation == 'SetStartTimeNextHeat' ? 5 : this.GPI2Operation == 'AttachAndRoundToWholeMinute' ? 6 : this.GPI2Operation == 'SetStartTimeParticipantWithBaasEqPort' ? 7 : this.GPI2Operation == 'SetStartTimeNextArmedHeat' ? 8 : this.GPI2Operation == 'SetStartNextParticipantOrNextArmedHeat' ? 9 : 0}` +
            ',' +
            `"GPI2Value1": ${JSON.stringify(this.GPI2Value1)}` +
            ',' +
            `"GPI2Value2": ${JSON.stringify(this.GPI2Value2)}` +
            ',' +
            `"GPI3Operation": ${this.GPI3Operation == 'None' ? 0 : this.GPI3Operation == 'AttachToTime' ? 1 : this.GPI3Operation == 'SetStartTimeNextGroup' ? 2 : this.GPI3Operation == 'SetStartTimeParticipant' ? 4 : this.GPI3Operation == 'SetStartTimeNextHeat' ? 5 : this.GPI3Operation == 'AttachAndRoundToWholeMinute' ? 6 : this.GPI3Operation == 'SetStartTimeParticipantWithBaasEqPort' ? 7 : this.GPI3Operation == 'SetStartTimeNextArmedHeat' ? 8 : this.GPI3Operation == 'SetStartNextParticipantOrNextArmedHeat' ? 9 : 0}` +
            ',' +
            `"GPI3Value1": ${JSON.stringify(this.GPI3Value1)}` +
            ',' +
            `"GPI3Value2": ${JSON.stringify(this.GPI3Value2)}` +
            ',' +
            `"GPI4Operation": ${this.GPI4Operation == 'None' ? 0 : this.GPI4Operation == 'AttachToTime' ? 1 : this.GPI4Operation == 'SetStartTimeNextGroup' ? 2 : this.GPI4Operation == 'SetStartTimeParticipant' ? 4 : this.GPI4Operation == 'SetStartTimeNextHeat' ? 5 : this.GPI4Operation == 'AttachAndRoundToWholeMinute' ? 6 : this.GPI4Operation == 'SetStartTimeParticipantWithBaasEqPort' ? 7 : this.GPI4Operation == 'SetStartTimeNextArmedHeat' ? 8 : this.GPI4Operation == 'SetStartNextParticipantOrNextArmedHeat' ? 9 : 0}` +
            ',' +
            `"GPI4Value1": ${JSON.stringify(this.GPI4Value1)}` +
            ',' +
            `"GPI4Value2": ${JSON.stringify(this.GPI4Value2)}` +
            ',' +
            `"GPSCreatePassRadius": ${JSON.stringify(this.GPSCreatePassRadius)}` +
            ',' +
            `"IgnoreInSplitresult": ${JSON.stringify(this.IgnoreInSplitresult)}` +
            ',' +
            `"IsNeutralZoneIn": ${JSON.stringify(this.IsNeutralZoneIn)}` +
            ',' +
            `"IsNeutralZoneOut": ${JSON.stringify(this.IsNeutralZoneOut)}` +
            ',' +
            `"IsShootingStation": ${JSON.stringify(this.IsShootingStation)}` +
            ',' +
            `"Km": ${JSON.stringify(this.Km)}` +
            ',' +
            `"Mandatory": ${JSON.stringify(this.Mandatory)}` +
            ',' +
            `"MaxLapTime": ${this.MaxLapTime ? '"' + this.MaxLapTime?.serialize() + '"' : null}` +
            ',' +
            `"MinLapTime": ${'"' + this.MinLapTime?.serialize() + '"'}` +
            ',' +
            `"Name": ${JSON.stringify(this.Name)}` +
            ',' +
            `"Overrides": [ ${this.Overrides.map((x) => (!x ? null : x?.serialize()))} ]` +
            ',' +
            `"PhotofinishMilliseconds": ${JSON.stringify(this.PhotofinishMilliseconds)}` +
            ',' +
            `"Pretiming": ${JSON.stringify(this.Pretiming)}` +
            ',' +
            `"PrintOnResultlist": ${JSON.stringify(this.PrintOnResultlist)}` +
            ',' +
            `"Public": ${JSON.stringify(this.Public)}` +
            ',' +
            `"RaceID": ${JSON.stringify(this.RaceID)}` +
            ',' +
            `"RelayLegnumber": ${JSON.stringify(this.RelayLegnumber)}` +
            ',' +
            `"SmoothingInSeconds": ${JSON.stringify(this.SmoothingInSeconds)}` +
            ',' +
            `"Sort": ${JSON.stringify(this.Sort)}` +
            ',' +
            `"Start": ${JSON.stringify(this.Start)}` +
            ',' +
            `"StartControl": ${JSON.stringify(this.StartControl)}` +
            ',' +
            `"StartControlMax": ${JSON.stringify(this.StartControlMax)}` +
            ',' +
            `"StartControlMin": ${JSON.stringify(this.StartControlMin)}` +
            ',' +
            `"StartNet": ${JSON.stringify(this.StartNet)}` +
            ',' +
            `"StartTime": ${'"' + formatDateIso(this.StartTime) + '"'}` +
            ',' +
            `"StationID": ${JSON.stringify(this.StationID)}` +
            ',' +
            `"StopTime": ${'"' + formatDateIso(this.StopTime) + '"'}` +
            ',' +
            `"TimeSelect": ${JSON.stringify(this.TimeSelect)}` +
            ',' +
            `"ChangedColumns": ${unref(this.__selfEntityState) !== 'Modified' ? 'null' : JSON.stringify(this.__entityStateHandler.getChangedPropertiesArray())}` +
            '}'
        );
    }

    /**
     * Sets the entity state to Deleted
     */
    public Delete() {
        if (this.__selfEntityState === 'Added') {
            throw new Error('Cannot delete an object that is in Added state. It has not been saved yet.');
        }
        this.__selfEntityState = 'Deleted';
        this.__entityStateHandler.entityStateChanged(this.__selfEntityState);
    }

    /**
     * Undeletes the object by setting resetting the EntityState
     *
     * If the object has modifications, the new state will be 'Modified', otherwise 'Unchanged'
     */
    public DeleteUndo() {
        this.__selfEntityState = 'Unchanged';
        this.__entityStateHandler.entityStateChanged(this.__selfEntityState);
    }

    /**
     * Sets the entity state to Added
     *
     * Also sets the ID to the supplied ID. If no id parameter supplied, the previous id is kept
     *
     * Note: This does not do anything with the children, so use with caution
     *
     * The use case of this is to make copies of existing objects as new objects without having to fill every field.
     */
    public SetEntityStateAsAddedAndResetId(id?: number) {
        if (this.EntityState !== 'Unchanged') {
            throw new Error('Changing a modified object to Added is not supported.');
        }
        this.__selfEntityState = 'Added';
        if (id !== undefined) {
            this.__id = id;
        }
        this.__entityStateHandler.entityStateChanged(this.__selfEntityState);
    }

    /**
     * Creates an instance of StationRole from the supplied values.
     * Missing values have default values
     * @param values The values you want to set
     * @returns An object instance with the supplied values set
     */
    public static FromJsValues(values: {
        ID?: number;
        AcceptChipForRelayLegnumber?: number | null;
        AllowNegativeStart?: boolean;
        AllowOnlyGPIOOrManual?: boolean;
        AssignLapPoints?: number | null;
        AutomaticallyChangeRace?: boolean;
        EventID?: number;
        Finish?: boolean;
        GPI1Operation?: StationRoleGPIOoperation;
        GPI1Value1?: number;
        GPI1Value2?: number;
        GPI2Operation?: StationRoleGPIOoperation;
        GPI2Value1?: number;
        GPI2Value2?: number;
        GPI3Operation?: StationRoleGPIOoperation;
        GPI3Value1?: number;
        GPI3Value2?: number;
        GPI4Operation?: StationRoleGPIOoperation;
        GPI4Value1?: number;
        GPI4Value2?: number;
        GPSCreatePassRadius?: number | null;
        IgnoreInSplitresult?: boolean;
        IsNeutralZoneIn?: boolean;
        IsNeutralZoneOut?: boolean;
        IsShootingStation?: boolean;
        Km?: number;
        Mandatory?: boolean;
        MaxLapTime?: ITimeSpan | null;
        MinLapTime?: ITimeSpan;
        Name?: string | null;
        Overrides?: StationRoleOverride[];
        PhotofinishMilliseconds?: number | null;
        Pretiming?: boolean;
        PrintOnResultlist?: boolean;
        Public?: boolean;
        RaceID?: number | null;
        RelayLegnumber?: number | null;
        SmoothingInSeconds?: number;
        Sort?: number;
        Start?: boolean;
        StartControl?: boolean;
        StartControlMax?: number | null;
        StartControlMin?: number | null;
        StartNet?: boolean;
        StartTime?: Date;
        StationID?: number;
        StopTime?: Date;
        TimeSelect?: number;
        EntityState?: 'Added' | 'Unchanged';
    }): StationRole {
        return new StationRole(values);
    }

    public static _FromServerJson(json: any): StationRole {
        return new StationRole({
            ID: json.ID,
            AcceptChipForRelayLegnumber: json.AcceptChipForRelayLegnumber ?? null,
            AllowNegativeStart: json.AllowNegativeStart,
            AllowOnlyGPIOOrManual: json.AllowOnlyGPIOOrManual,
            AssignLapPoints: json.AssignLapPoints ?? null,
            AutomaticallyChangeRace: json.AutomaticallyChangeRace,
            EventID: json.EventID,
            Finish: json.Finish,
            GPI1Operation:
                json.GPI1Operation == 0
                    ? 'None'
                    : json.GPI1Operation == 1
                      ? 'AttachToTime'
                      : json.GPI1Operation == 2
                        ? 'SetStartTimeNextGroup'
                        : json.GPI1Operation == 4
                          ? 'SetStartTimeParticipant'
                          : json.GPI1Operation == 5
                            ? 'SetStartTimeNextHeat'
                            : json.GPI1Operation == 6
                              ? 'AttachAndRoundToWholeMinute'
                              : json.GPI1Operation == 7
                                ? 'SetStartTimeParticipantWithBaasEqPort'
                                : json.GPI1Operation == 8
                                  ? 'SetStartTimeNextArmedHeat'
                                  : json.GPI1Operation == 9
                                    ? 'SetStartNextParticipantOrNextArmedHeat'
                                    : 'None',
            GPI1Value1: json.GPI1Value1,
            GPI1Value2: json.GPI1Value2,
            GPI2Operation:
                json.GPI2Operation == 0
                    ? 'None'
                    : json.GPI2Operation == 1
                      ? 'AttachToTime'
                      : json.GPI2Operation == 2
                        ? 'SetStartTimeNextGroup'
                        : json.GPI2Operation == 4
                          ? 'SetStartTimeParticipant'
                          : json.GPI2Operation == 5
                            ? 'SetStartTimeNextHeat'
                            : json.GPI2Operation == 6
                              ? 'AttachAndRoundToWholeMinute'
                              : json.GPI2Operation == 7
                                ? 'SetStartTimeParticipantWithBaasEqPort'
                                : json.GPI2Operation == 8
                                  ? 'SetStartTimeNextArmedHeat'
                                  : json.GPI2Operation == 9
                                    ? 'SetStartNextParticipantOrNextArmedHeat'
                                    : 'None',
            GPI2Value1: json.GPI2Value1,
            GPI2Value2: json.GPI2Value2,
            GPI3Operation:
                json.GPI3Operation == 0
                    ? 'None'
                    : json.GPI3Operation == 1
                      ? 'AttachToTime'
                      : json.GPI3Operation == 2
                        ? 'SetStartTimeNextGroup'
                        : json.GPI3Operation == 4
                          ? 'SetStartTimeParticipant'
                          : json.GPI3Operation == 5
                            ? 'SetStartTimeNextHeat'
                            : json.GPI3Operation == 6
                              ? 'AttachAndRoundToWholeMinute'
                              : json.GPI3Operation == 7
                                ? 'SetStartTimeParticipantWithBaasEqPort'
                                : json.GPI3Operation == 8
                                  ? 'SetStartTimeNextArmedHeat'
                                  : json.GPI3Operation == 9
                                    ? 'SetStartNextParticipantOrNextArmedHeat'
                                    : 'None',
            GPI3Value1: json.GPI3Value1,
            GPI3Value2: json.GPI3Value2,
            GPI4Operation:
                json.GPI4Operation == 0
                    ? 'None'
                    : json.GPI4Operation == 1
                      ? 'AttachToTime'
                      : json.GPI4Operation == 2
                        ? 'SetStartTimeNextGroup'
                        : json.GPI4Operation == 4
                          ? 'SetStartTimeParticipant'
                          : json.GPI4Operation == 5
                            ? 'SetStartTimeNextHeat'
                            : json.GPI4Operation == 6
                              ? 'AttachAndRoundToWholeMinute'
                              : json.GPI4Operation == 7
                                ? 'SetStartTimeParticipantWithBaasEqPort'
                                : json.GPI4Operation == 8
                                  ? 'SetStartTimeNextArmedHeat'
                                  : json.GPI4Operation == 9
                                    ? 'SetStartNextParticipantOrNextArmedHeat'
                                    : 'None',
            GPI4Value1: json.GPI4Value1,
            GPI4Value2: json.GPI4Value2,
            GPSCreatePassRadius: json.GPSCreatePassRadius ?? null,
            IgnoreInSplitresult: json.IgnoreInSplitresult,
            IsNeutralZoneIn: json.IsNeutralZoneIn,
            IsNeutralZoneOut: json.IsNeutralZoneOut,
            IsShootingStation: json.IsShootingStation,
            Km: json.Km,
            Mandatory: json.Mandatory,
            MaxLapTime: json.MaxLapTime ? TimeSpan.FromTimeString(json.MaxLapTime) : null,
            MinLapTime: json.MinLapTime ? TimeSpan.FromTimeString(json.MinLapTime) : TimeSpan.Empty(),
            Name: json.Name ?? '',
            Overrides: json.Overrides?.map((x: any) => StationRoleOverride._FromServerJson(x)),
            PhotofinishMilliseconds: json.PhotofinishMilliseconds ?? null,
            Pretiming: json.Pretiming,
            PrintOnResultlist: json.PrintOnResultlist,
            Public: json.Public,
            RaceID: json.RaceID ?? null,
            RelayLegnumber: json.RelayLegnumber ?? null,
            SmoothingInSeconds: json.SmoothingInSeconds,
            Sort: json.Sort,
            Start: json.Start,
            StartControl: json.StartControl,
            StartControlMax: json.StartControlMax ?? null,
            StartControlMin: json.StartControlMin ?? null,
            StartNet: json.StartNet,
            StartTime: json.StartTime instanceof Date ? json.StartTime : new Date(json.StartTime),
            StationID: json.StationID,
            StopTime: json.StopTime instanceof Date ? json.StopTime : new Date(json.StopTime),
            TimeSelect: json.TimeSelect,
            EntityState: 'Unchanged',
        });
    }

    /**
     * Creates an instance of StationRole from another instance of StationRole.
     * @param other The StationRole you want to clone
     * @returns A cloned StationRole
     */
    public static FromOther(other: StationRole): StationRole {
        if (other.EntityState !== 'Unchanged') {
            throw new Error('Cloning an object with EntityState other than Unchanged is not supported');
        }
        return StationRole._FromServerJson(JSON.parse(other.serialize()));
    }

    /**
     * Deepclones the object.
     */
    public DeepClone() {
        return StationRole.FromOther(this);
    }

    /**
     * IsPropertyChanged.
     */
    public IsPropertyChanged(
        ...props: (
            | 'ID'
            | 'AcceptChipForRelayLegnumber'
            | 'AllowNegativeStart'
            | 'AllowOnlyGPIOOrManual'
            | 'AssignLapPoints'
            | 'AutomaticallyChangeRace'
            | 'EventID'
            | 'Finish'
            | 'GPI1Operation'
            | 'GPI1Value1'
            | 'GPI1Value2'
            | 'GPI2Operation'
            | 'GPI2Value1'
            | 'GPI2Value2'
            | 'GPI3Operation'
            | 'GPI3Value1'
            | 'GPI3Value2'
            | 'GPI4Operation'
            | 'GPI4Value1'
            | 'GPI4Value2'
            | 'GPSCreatePassRadius'
            | 'IgnoreInSplitresult'
            | 'IsNeutralZoneIn'
            | 'IsNeutralZoneOut'
            | 'IsShootingStation'
            | 'Km'
            | 'Mandatory'
            | 'MaxLapTime'
            | 'MinLapTime'
            | 'Name'
            | 'Overrides'
            | 'PhotofinishMilliseconds'
            | 'Pretiming'
            | 'PrintOnResultlist'
            | 'Public'
            | 'RaceID'
            | 'RelayLegnumber'
            | 'SmoothingInSeconds'
            | 'Sort'
            | 'Start'
            | 'StartControl'
            | 'StartControlMax'
            | 'StartControlMin'
            | 'StartNet'
            | 'StartTime'
            | 'StationID'
            | 'StopTime'
            | 'TimeSelect'
        )[]
    ) {
        return this.__entityStateHandler.isPropertyChanged(...props);
    }
    static NAME_OF_CLASS = 'StationRole';
}
